import settings from './network.settings'
import enableTilt from './network.tilt'

const canvas = document.getElementById('network')
const ctx = canvas?.getContext('2d')

if (ctx) {
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)')
  const pointer = { x: 0, y: 0 }
  const offset = { x: 0, y: 0 }
  const particles = []
  const palette = settings.colors.palette
  const rootStyle = document.documentElement.style

  for (const [ section, hue ] of Object.entries(settings.colors.sectionHue)) {
    rootStyle.setProperty(`--network-hue-${section}`, `${hue}deg`)
  }
  rootStyle.setProperty('--network-color-duration', `${settings.colors.transitionSeconds}s`)
  const buckets = Array.from({ length: palette.length * 16 }, () => [])
  const glow = document.createElement('canvas')
  const glowCtx = glow.getContext('2d')
  const stars = []
  const sprites = palette.map((color) => {
    const sprite = document.createElement('canvas')
    const context = sprite.getContext('2d')

    sprite.width = 64
    sprite.height = 64
    const gradient = context.createRadialGradient(32, 32, 0, 32, 32, 32)

    gradient.addColorStop(0, `rgba(${color}, .6)`)
    gradient.addColorStop(.15, `rgba(${color}, .25)`)
    gradient.addColorStop(.45, `rgba(${color}, .07)`)
    gradient.addColorStop(1, `rgba(${color}, 0)`)
    context.fillStyle = gradient
    context.fillRect(0, 0, 64, 64)

    return sprite
  })

  // Ordered strands, rather than random dots, reveal the flowing surface.
  const dotCount = Math.max(0, Math.floor(settings.dots.count))
  const strands = Math.max(1, Math.min(dotCount, Math.floor(settings.dots.strands)))

  for (let strand = 0; strand < strands; strand++) {
    const steps = Math.floor(dotCount / strands) + Number(strand < dotCount % strands)

    for (let step = 0; step < steps; step++) {
      const u = strand / strands * Math.PI * 2
      const v = (step + .5) / steps * Math.PI

      particles.push({ u, v, strand, step })
    }
  }
  // A sparse graph attached to the existing surface follows both morph and rotation.
  const anchors = particles.filter((particle, index) => index % Math.max(1, Math.floor(dotCount / settings.network.anchors)) === 0)
  const links = []

  anchors.forEach((a, index) => {
    const neighbors = anchors.map((b, other) => {
      const distance = Math.hypot(
        Math.sin(a.v) * Math.cos(a.u) - Math.sin(b.v) * Math.cos(b.u),
        Math.cos(a.v) - Math.cos(b.v),
        Math.sin(a.v) * Math.sin(a.u) - Math.sin(b.v) * Math.sin(b.u)
      )

      return { other, distance }
    }).filter((item) => item.other !== index && item.distance > .12 && item.distance < .85)
      .sort((first, second) => first.distance - second.distance).slice(0, 2)

    for (const { other } of neighbors) {
      if (!links.some((link) => link.a === other && link.b === index)) {
        links.push({ a: index, b: other, phase: (index * .618 + other * .37) % 1 })
      }
    }
  })
  const labels = settings.network.words.map((word, index) => ({
    word: word.toUpperCase(),
    node: anchors[Math.floor((index + .5) * anchors.length / settings.network.words.length)]
  })).filter((label) => label.node)

  for (let i = 0; i < settings.stars.count; i++) {
    stars.push({
      x: Math.sin(i * 127.1) * .5 + .5,
      y: Math.sin(i * 311.7) * .5 + .5,
      size: .4 + (i % 5) * .3
    })
  }
  let width = 0
  let height = 0
  let frame = 0
  let lastTime = 0
  let time = 0

  function draw() {
    ctx.clearRect(0, 0, width, height)
    // Hold each silhouette, then ease into the next.
    const { sphereHold, torusHold, sphereRest } = settings.animation
    const duration = Math.max(.001, settings.animation.morphDuration)
    const returnStart = sphereHold + duration + torusHold
    const cycle = time % (returnStart + duration + sphereRest)
    const transition = Math.max(0, Math.min(1, (cycle - sphereHold) / duration))
    const returning = Math.max(0, Math.min(1, (cycle - returnStart) / duration))
    const morph = transition * transition * (3 - 2 * transition) - returning * returning * (3 - 2 * returning)
    const yaw = time * settings.animation.rotationSpeed + offset.x
    const pitch = settings.shape.tilt + offset.y + Math.sin(time * .13) * .16
    const roll = .15 + Math.sin(time * .08) * .12
    const cy = Math.cos(yaw)
    const sy = Math.sin(yaw)
    const cx = Math.cos(pitch)
    const sx = Math.sin(pitch)
    const cr = Math.cos(roll)
    const sr = Math.sin(roll)
    const scale = Math.min(width * settings.shape.width, height * settings.shape.height)

    for (const bucket of buckets) {
      bucket.length = 0
    }

    glowCtx.clearRect(0, 0, glow.width, glow.height)
    for (let i = 0; i < stars.length; i++) {
      const star = stars[i]
      const x = star.x * width + offset.x * (i % 12)
      const y = star.y * height + offset.y * (i % 12)

      ctx.fillStyle = `rgba(${settings.colors.stars}, ${.15 + .1 * Math.sin(time * .4 + i)})`
      ctx.beginPath()
      ctx.arc(x, y, star.size, 0, Math.PI * 2)
      ctx.fill()
    }

    for (const particle of particles) {
      const { u, v, strand, step } = particle
      const ripple = Math.sin(u * 5 + v * 4 + time * .65)
      const fold = Math.sin(v * 7 - u * 3 - time * .4)
      const radius = 1 + ripple * settings.shape.ripple + fold * settings.shape.fold
      const twist = u + settings.shape.twist * Math.sin(v * 4 + time * .3)
      const sphereX = Math.sin(v) * Math.cos(twist) * radius
      const sphereY = Math.cos(v) * radius
      const sphereZ = Math.sin(v) * Math.sin(twist) * radius
      const tube = v * 2
      const ring = .7 + (.28 + ripple * .045) * Math.cos(tube)
      const torusX = ring * Math.cos(twist)
      const torusY = .28 * Math.sin(tube) + fold * settings.shape.fold
      const torusZ = ring * Math.sin(twist)
      const x = sphereX + (torusX - sphereX) * morph
      const y = sphereY + (torusY - sphereY) * morph
      const z = sphereZ + (torusZ - sphereZ) * morph
      const rotatedX = x * cy + z * sy
      const rotatedZ = z * cy - x * sy
      const rotatedY = y * cx - rotatedZ * sx
      const depth = y * sx + rotatedZ * cx
      const perspective = 3.5 / (3.5 - depth)
      const screenX = width * .5 + (rotatedX * cr - rotatedY * sr) * scale * perspective
      const screenY = height * settings.shape.centerY + (rotatedX * sr + rotatedY * cr) * scale * perspective
      const depthIndex = Math.max(0, Math.min(15, Math.floor((depth + 1.2) * 6.25)))
      const colorWave = Math.sin(u * 3 + v * 5 + time * settings.colors.cycleSpeed)
      const color = Math.max(0, Math.min(palette.length - 1, Math.floor((colorWave + 1) * palette.length / 2)))

      particle.screenX = screenX
      particle.screenY = screenY
      particle.depth = depth
      particle.color = color
      particle.perspective = perspective
      buckets[color * 16 + depthIndex].push(screenX, screenY)

      // Low-resolution bloom and occasional defocused foreground beads.
      if (settings.glow.enabled && step % 6 === 0 && strand % 2 === 0) {
        const size = (12 + depthIndex * 1.8) * Math.max(.65, scale / 500)

        glowCtx.drawImage(sprites[color], (screenX - size) / 3, (screenY - size) / 3, size * 2 / 3, size * 2 / 3)
      }
      if (settings.glow.bokeh && depth > .65 && strand % 9 === 0 && step % 7 === 0) {
        const size = 5 + (depth - .65) * 22

        ctx.drawImage(sprites[color], screenX - size, screenY - size, size * 2, size * 2)
      }
    }

    drawConnections(scale)
    ctx.globalCompositeOperation = 'lighter'
    ctx.globalAlpha = settings.glow.intensity
    ctx.drawImage(glow, 0, 0, width, height)
    ctx.globalAlpha = 1
    for (let i = 0; i < buckets.length; i++) {
      const bucket = buckets[i]
      const depth = i % 16
      const color = Math.floor(i / 16)
      const radius = settings.dots.size * (.45 + depth * .075) * Math.max(.7, Math.min(1.2, scale / 450))

      ctx.fillStyle = `rgba(${palette[color]}, ${(.13 + depth * .042) * settings.dots.opacity})`
      ctx.beginPath()
      for (let j = 0; j < bucket.length; j += 2) {
        ctx.moveTo(bucket[j] + radius, bucket[j + 1])
        ctx.arc(bucket[j], bucket[j + 1], radius, 0, Math.PI * 2)
      }
      ctx.fill()
    }
    ctx.globalCompositeOperation = 'source-over'
    drawLabels()
  }

  function drawConnections(scale) {
    for (const link of links) {
      const a = anchors[link.a]
      const b = anchors[link.b]
      const distance = Math.hypot(a.screenX - b.screenX, a.screenY - b.screenY)
      const fade = Math.max(0, 1 - distance / Math.max(1, scale * .85))
      const visibility = Math.max(.4, Math.min(1, .7 + (a.depth + b.depth) * .2)) * fade
      const color = palette[a.color]

      ctx.lineWidth = 1.2
      ctx.strokeStyle = `rgba(${color}, ${settings.network.lineOpacity * visibility})`
      ctx.beginPath()
      ctx.moveTo(a.screenX, a.screenY)
      ctx.lineTo(b.screenX, b.screenY)
      ctx.stroke()
      const progress = (time * settings.network.pulseSpeed + link.phase * 3) % 3

      if (progress < 1 && distance > 1) {
        const x = a.screenX + (b.screenX - a.screenX) * progress
        const y = a.screenY + (b.screenY - a.screenY) * progress
        const trail = Math.max(0, progress - .2)
        const alpha = Math.sin(progress * Math.PI) * visibility
        const gradient = ctx.createLinearGradient(a.screenX, a.screenY, b.screenX, b.screenY)

        gradient.addColorStop(trail, `rgba(${color}, 0)`)
        gradient.addColorStop(progress, `rgba(${color}, ${alpha})`)
        ctx.strokeStyle = gradient
        ctx.lineWidth = 1.8
        ctx.beginPath()
        ctx.moveTo(a.screenX + (b.screenX - a.screenX) * trail, a.screenY + (b.screenY - a.screenY) * trail)
        ctx.lineTo(x, y)
        ctx.stroke()
        ctx.globalAlpha = alpha
        ctx.drawImage(sprites[a.color], x - 10, y - 10, 20, 20)
        ctx.globalAlpha = 1
        ctx.fillStyle = `rgba(${color}, ${alpha})`
        ctx.beginPath()
        ctx.arc(x, y, 1.8, 0, Math.PI * 2)
        ctx.fill()
      }
    }
  }

  function drawLabels() {
    const occupied = []

    ctx.textBaseline = 'middle'
    for (const { word, node } of labels) {
      const x = node.screenX + 10
      const y = node.screenY
      const edge = Math.min(1, Math.abs(x - width * .5) / (width * .25))
      const alpha = settings.network.wordOpacity * Math.max(.35, Math.min(1, (node.depth + 1.4) / 2)) * edge
      const size = (width < 600 ? 10 : 12) * node.perspective

      ctx.font = `${size}px monospace`
      const labelWidth = ctx.measureText(word).width

      if (occupied.some((box) => x < box.x + box.width + 12 && x + labelWidth + 12 > box.x && Math.abs(y - box.y) < size + 8)) {
        continue
      }
      occupied.push({ x, y, width: labelWidth })
      ctx.fillStyle = `rgba(${palette[node.color]}, ${alpha})`
      ctx.fillText(word, x, y)
    }
  }

  function animate(now) {
    if (now - lastTime >= 1000 / settings.performance.fps) {
      time += Math.min((now - lastTime) / 1000, .1) * settings.animation.speed
      lastTime = now
      offset.x += (pointer.x - offset.x) * settings.mouse.easing
      offset.y += (pointer.y - offset.y) * settings.mouse.easing
      draw()
    }
    frame = requestAnimationFrame(animate)
  }

  function refresh() {
    cancelAnimationFrame(frame)
    if (motion.matches) {
      offset.x = 0
      offset.y = 0
    }
    draw()
    if (!motion.matches && !document.hidden) {
      lastTime = performance.now()
      frame = requestAnimationFrame(animate)
    }
  }

  function resize() {
    width = window.innerWidth
    height = window.innerHeight
    const ratio = Math.min(window.devicePixelRatio || 1, settings.performance.pixelRatio)

    canvas.width = Math.round(width * ratio)
    canvas.height = Math.round(height * ratio)
    glow.width = Math.ceil(width / 3)
    glow.height = Math.ceil(height / 3)
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
    refresh()
  }

  window.addEventListener('pointermove', (event) => {
    if (event.pointerType === 'mouse' && !motion.matches) {
      pointer.x = (event.clientX / width - .5) * settings.mouse.horizontal
      pointer.y = (event.clientY / height - .5) * settings.mouse.vertical
    }
  }, { passive: true })
  document.addEventListener('pointerleave', () => {
    pointer.x = 0
    pointer.y = 0
  })
  window.addEventListener('resize', resize)
  document.addEventListener('visibilitychange', refresh)
  motion.addEventListener('change', refresh)
  resize()
  enableTilt(pointer, motion)
}
